import { type Component, type JSX, Show, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import { useModalTrap } from '~/shared/ui/focus-trap';

const escStack: Array<() => void> = [];

export const sidePanelDepth = (): number => escStack.length;

export interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  width?: string;
  zIndex?: number;
  ariaLabel?: string;
  dismissOnBackdrop?: boolean;
  dismissOnEscape?: boolean;
  bodyScroll?: boolean;
  class?: string;
  children: JSX.Element;
}

const SidePanelInner: Component<SidePanelProps> = (props) => {
  if (props.dismissOnEscape !== false) {
    const close = () => props.onClose();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (escStack[escStack.length - 1] !== close) return;
      e.preventDefault();
      e.stopPropagation();
      props.onClose();
    };
    onMount(() => {
      escStack.push(close);
      window.addEventListener('keydown', onKey);
    });
    onCleanup(() => {
      const i = escStack.lastIndexOf(close);
      if (i >= 0) escStack.splice(i, 1);
      window.removeEventListener('keydown', onKey);
    });
  }

  return (
    <div
      ref={useModalTrap}
      class="fixed inset-0 flex justify-end"
      style={{ 'z-index': String(props.zIndex ?? 1090) }}
      role="dialog"
      aria-modal="true"
      aria-label={props.ariaLabel}
    >
      <button
        type="button"
        class="absolute inset-0 bg-black/40 animate-fade-in"
        onClick={() => {
          if (props.dismissOnBackdrop !== false) props.onClose();
        }}
      >
        <span class="sr-only">Закрыть</span>
      </button>
      <div
        class="side-panel-column relative w-full h-full"
        style={{
          'max-width': props.width ?? 'var(--sidebar-width)',
          'border-radius': '0',
          'border-top-left-radius': 'var(--radius-2xl)',
          'border-bottom-left-radius': 'var(--radius-2xl)',
        }}
      >
        <div
          aria-hidden="true"
          class="side-panel-glass glass-strong side-panel-fade absolute inset-0"
          style={{ 'border-radius': 'inherit' }}
        />
        <aside
          class={`side-panel-surface side-panel-fade relative w-full h-full ${
            props.bodyScroll === false ? '' : 'overflow-y-auto'
          } ${props.class ?? ''}`}
          style={{ 'border-radius': 'inherit' }}
        >
          {props.children}
        </aside>
      </div>
    </div>
  );
};

export const SidePanel: Component<SidePanelProps> = (props) => (
  <Portal>
    <Show when={props.open}>
      <SidePanelInner {...props} />
    </Show>
  </Portal>
);
